import React, { useState } from 'react';
import { HelpCircle, ChevronRight, CheckCircle2, RotateCcw, Trophy } from 'lucide-react';

export interface QuizQuestion {
    id: string;
    question: string;
    options: {
        id: string;
        text: string;
        points: Record<string, number>; // Maps result ID to points
    }[];
}

export interface QuizResult {
    id: string;
    title: string;
    description: string;
    icon?: string;
    link?: string;
}

interface RecommendationQuizProps {
    questions: QuizQuestion[];
    results: QuizResult[];
    title?: string;
    subtitle?: string;
}

const RecommendationQuiz: React.FC<RecommendationQuizProps> = ({
    questions,
    results,
    title = "Qual é a melhor opção para você?",
    subtitle = "Responda algumas perguntas rápidas e descubra!"
}) => {
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [scores, setScores] = useState<Record<string, number>>({});
    const [showResult, setShowResult] = useState(false);
    const [isStarted, setIsStarted] = useState(false);

    const handleAnswer = (questionId: string, optionId: string, points: Record<string, number>) => {
        // Update answers
        const newAnswers = { ...answers, [questionId]: optionId };
        setAnswers(newAnswers);

        // Update scores
        const newScores = { ...scores };
        Object.entries(points).forEach(([resultId, pointValue]) => {
            newScores[resultId] = (newScores[resultId] || 0) + pointValue;
        });
        setScores(newScores);

        // Move to next question or show result
        if (currentQuestion < questions.length - 1) {
            setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
        } else {
            setTimeout(() => setShowResult(true), 300);
        }
    };

    const getWinner = (): QuizResult | null => {
        if (Object.keys(scores).length === 0) return null;

        const winnerId = Object.entries(scores).reduce((a, b) =>
            a[1] > b[1] ? a : b
        )[0];

        return results.find(r => r.id === winnerId) || null;
    };

    const resetQuiz = () => {
        setCurrentQuestion(0);
        setAnswers({});
        setScores({});
        setShowResult(false);
        setIsStarted(false);
    };

    // Start screen
    if (!isStarted) {
        return (
            <div className="my-8 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-8 text-white text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <HelpCircle className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{title}</h3>
                <p className="text-purple-100 mb-6">{subtitle}</p>
                <p className="text-sm text-purple-200 mb-6">
                    ⏱️ {questions.length} perguntas · Menos de 1 minuto
                </p>
                <button
                    onClick={() => setIsStarted(true)}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-white text-purple-700 font-semibold rounded-xl hover:bg-purple-50 transition-colors"
                >
                    Começar Quiz
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>
        );
    }

    // Result screen
    if (showResult) {
        const winner = getWinner();

        return (
            <div className="my-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-8 text-white">
                <div className="text-center">
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Trophy className="w-10 h-10" />
                    </div>
                    <p className="text-green-100 mb-2">Nossa recomendação para você:</p>
                    <h3 className="text-3xl font-bold mb-4">
                        {winner?.icon} {winner?.title}
                    </h3>
                    <p className="text-green-100 max-w-md mx-auto mb-8">
                        {winner?.description}
                    </p>

                    {/* Score breakdown */}
                    <div className="bg-white/10 rounded-xl p-6 mb-6">
                        <p className="text-sm text-green-100 mb-4">Pontuação por opção:</p>
                        <div className="flex flex-wrap justify-center gap-4">
                            {results.map(result => (
                                <div
                                    key={result.id}
                                    className={`px-4 py-2 rounded-lg ${result.id === winner?.id
                                            ? 'bg-white text-green-700'
                                            : 'bg-white/20 text-white'
                                        }`}
                                >
                                    <span className="font-medium">{result.title}</span>
                                    <span className="ml-2 font-bold">{scores[result.id] || 0} pts</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={resetQuiz}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 text-white rounded-xl hover:bg-white/30 transition-colors"
                    >
                        <RotateCcw className="w-4 h-4" />
                        Refazer Quiz
                    </button>
                </div>
            </div>
        );
    }

    // Question screen
    const question = questions[currentQuestion];
    const progress = ((currentQuestion) / questions.length) * 100;

    return (
        <div className="my-8 bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
            {/* Progress bar */}
            <div className="h-2 bg-gray-100">
                <div
                    className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                />
            </div>

            <div className="p-8">
                {/* Question counter */}
                <div className="text-sm text-gray-500 mb-4">
                    Pergunta {currentQuestion + 1} de {questions.length}
                </div>

                {/* Question */}
                <h4 className="text-xl font-bold text-gray-900 mb-6">
                    {question.question}
                </h4>

                {/* Options */}
                <div className="space-y-3">
                    {question.options.map((option) => {
                        const isSelected = answers[question.id] === option.id;
                        return (
                            <button
                                key={option.id}
                                onClick={() => handleAnswer(question.id, option.id, option.points)}
                                disabled={answers[question.id] !== undefined}
                                className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${isSelected
                                        ? 'border-purple-600 bg-purple-50'
                                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                                    } ${answers[question.id] !== undefined && !isSelected ? 'opacity-50' : ''}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected
                                            ? 'border-purple-600 bg-purple-600'
                                            : 'border-gray-300'
                                        }`}>
                                        {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                                    </div>
                                    <span className={`font-medium ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                                        {option.text}
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default RecommendationQuiz;
